import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Modal } from 'react-bootstrap';
import { ChevronLeft, KeyRound, MousePointerClick, Move, Plus } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import LevelNavigator from '../../Components/LevelNavigator';
import BlueprintWithMarkers, { NumberedMarker } from '../../Components/BlueprintWithMarkers';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useIsSmallScreen, useMediaQuery } from '../../hooks/useIsSmallScreen';
import { useListDetailScroll } from '../../hooks/useListDetailScroll';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useOpenPath } from '../../hooks/useOpenPath';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useCustomerType } from '../../hooks/useCustomerType';
import { useUnits } from '../../hooks/units';
import { useStairwells } from '../../hooks/stairwells/useStairwells';
import { useBuildingLeases } from '../../hooks/leases';
import { endedLeasesForObject, leasesPath } from '../../Helpers/leaseUtils';
import { useTenants } from '../../hooks/tenants';
import { BlueprintLocation, PhotoModel } from '../../types/protocols';
import { Unit, UnitCreateInput, UnitInput } from '../../types/units';
import { UnitKind } from '../../types/Enums';
import { uploadLeaseContract } from '../../Helpers/leaseContractUtils';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { nextNumberInSeries } from '../../Helpers/numberSeries';
import {
    unitStatusColor,
    unitTypeIcon,
    defaultUnitLevelKey,
    effectiveUnitLocations,
    isUnitLeasable,
    isUnitRented,
    movableUnitRoom,
    totalUnitArea,
    unitDisplayName,
    unitLevelKeys,
    unitLevelsDescending,
    unitsOnLevel,
    unitMarkerRented,
    vacantUnitCount,
} from '../../Helpers/unitUtils';
import LeaseModal, { LeaseModalSubmitData } from '../Leases/LeaseModal';
import UnitModal from './UnitModal';
import UnitList from './UnitList';
import UnitDetail from './UnitDetail';
import UnitDrawingsPane from './UnitDrawingsPane';
import StairwellsSection from './StairwellsSection';
import { unitSections } from './unitsView';
import { unitStairwellName } from '../../Helpers/stairwellUtils';
import './Units.css';

/**
 * Drawing interaction modes, each started and ended explicitly (as on the storage units page):
 * - new:   click the drawing to create units in series (each with its whole-unit room at the click, plan D13)
 * - move:  drag single-room units to new positions ("Flytta markörer"); the room moves (plan D5)
 */
type PlacementMode = { kind: 'none' } | { kind: 'new' } | { kind: 'move' };
type KindFilter = 'all' | UnitKind;

/** Wide enough for list, drawing and the selected unit's details side by side; narrower, Detaljer opens a modal. */
const DETAIL_PANE_QUERY = '(min-width: 1280px)';

const formatArea = (area: number): string => `${area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²`;

/**
 * A building's units (lägenheter and lokaler): /building/:id/units. The left
 * pane lists every unit of the building, grouped by trappuppgång; the right
 * pane shows a level's drawing with every unit on it (level selector,
 * placement), or, for the selected unit, each level its rooms are on with
 * those rooms marked (molnfast-react#1748). A click selects and keeps it
 * (`?unit=`); hovering changes nothing. On wide screens a third pane, always
 * shown, holds its details (narrower: the Detaljer modal). A unit is its rooms (plan D5): it shows on
 * every level a room of its is on, at the centroid of its placed rooms.
 * `?level=` deep-links a level.
 */
const UnitsPage: React.FC = () => {
    const { id: buildingKey } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const openPath = useOpenPath();
    const isAdmin = useIsAdmin();
    const customerType = useCustomerType();
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const unitsHook = useUnits({ buildingKey });
    const { units, sortedUnits, loading: loadingUnits, error, submitting, clearError, moveUnitRoom, refresh: refreshUnits } = unitsHook;
    const leaseHook = useBuildingLeases({ buildingKey, onMutated: refreshUnits });
    const { getTenantName } = useTenants();
    const stairwellsHook = useStairwells({ buildingKey });
    const { stairwells, sortedStairwells } = stairwellsHook;

    // The selected unit (list row + marker + drawing pane) lives in the URL (?unit=); a shared link shows it.
    // Placement and move mode set it too; the detail modal (`unitModalOpen`) opens from the pane's Detaljer.
    const selectedUnitKey = searchParams.get('unit');
    const [unitModalOpen, setUnitModalOpen] = useState(false);
    const setSelectedUnitKey = useCallback((unitKey: string | null) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (unitKey) next.set('unit', unitKey);
            else next.delete('unit');
            return next;
        }, { replace: true });
    }, [setSearchParams]);
    const [placement, setPlacement] = useState<PlacementMode>({ kind: 'none' });
    const [pendingLocation, setPendingLocation] = useState<BlueprintLocation | undefined>(undefined);
    const [lastCreatedUnitKey, setLastCreatedUnitKey] = useState<string | null>(null);
    const [showUnitModal, setShowUnitModal] = useState<'closed' | 'new' | 'edit'>('closed');
    const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
    const [vacantOnly, setVacantOnly] = useState(false);
    const [kindFilter, setKindFilter] = useState<KindFilter>('all');
    const [showLeaseModal, setShowLeaseModal] = useState(false);

    const isSmallScreen = useIsSmallScreen();

    useEffect(() => {
        if (buildingKey && (!currentBuilding || currentBuilding.key !== buildingKey)) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, currentBuilding, fetchBuilding]);

    const buildingName = currentBuilding?.summary?.name || 'Byggnad';
    useDocumentTitle(`Lägenheter & lokaler - ${buildingName}`);

    const levelsMap = useMemo(() => currentBuilding?.model.structure?.levels || {}, [currentBuilding]);
    const roomsMap = useMemo(() => currentBuilding?.model.structure?.rooms || {}, [currentBuilding]);
    const levels = useMemo(() => unitLevelsDescending(levelsMap), [levelsMap]);

    // Level selection: ?level= if valid, else the level of the ?unit= unit, else the default. Kept in the URL so it survives reloads.
    const requestedLevelKey = searchParams.get('level');
    const selectedLevelKey = useMemo(() => {
        if (requestedLevelKey && levels.some(l => l.key === requestedLevelKey)) return requestedLevelKey;
        const defaultLevelKey = defaultUnitLevelKey(levels, sortedUnits, roomsMap);
        const linkedUnit = selectedUnitKey ? units[selectedUnitKey] : undefined;
        if (!linkedUnit) return defaultLevelKey;
        // Stay on the default level when the unit is on it, so selecting a unit never moves the view.
        const linkedLevelKeys = unitLevelKeys(linkedUnit, roomsMap);
        if (defaultLevelKey && linkedLevelKeys.includes(defaultLevelKey)) return defaultLevelKey;
        return levels.find(l => linkedLevelKeys.includes(l.key))?.key ?? defaultLevelKey;
    }, [requestedLevelKey, selectedUnitKey, units, levels, sortedUnits, roomsMap]);
    // Switching level clears the selection, unless the caller keeps a unit selected across the switch.
    const selectLevel = useCallback((levelKey: string, keepUnitKey?: string) => {
        const next: Record<string, string> = {};
        if (levelKey) next.level = levelKey;
        if (keepUnitKey) next.unit = keepUnitKey;
        setSearchParams(next, { replace: true });
        setPlacement({ kind: 'none' });
        setPendingLocation(undefined);
    }, [setSearchParams]);

    const level = selectedLevelKey ? levelsMap[selectedLevelKey] : undefined;
    const currentBlueprintKey = level?.currentBlueprintKey;
    const currentBlueprint = level && currentBlueprintKey ? level.blueprints?.[currentBlueprintKey] : undefined;
    const hasDrawing = !!(currentBlueprint && currentBlueprintKey);

    const levelUnits = useMemo(() => (selectedLevelKey ? unitsOnLevel(sortedUnits, selectedLevelKey, roomsMap) : []), [sortedUnits, selectedLevelKey, roomsMap]);
    const selectedUnit = selectedUnitKey ? units[selectedUnitKey] : undefined;
    const selectedLease = selectedUnit?.currentLeaseKey
        ? leaseHook.leases.find(l => l.key === selectedUnit.currentLeaseKey)
        : undefined;
    // Closes when the unit leaves the selection (level switch, a ?unit= key that is not in the building), so a
    // unit selected later by placement does not open it.
    useEffect(() => {
        if (!loadingUnits && unitModalOpen && !selectedUnit) setUnitModalOpen(false);
    }, [loadingUnits, unitModalOpen, selectedUnit]);
    const selectedEndedLeases = isAdmin && selectedUnit
        ? endedLeasesForObject(leaseHook.leases, { type: 'unit', key: selectedUnit.key })
        : undefined;

    // Filters apply to the list (whole building) and the level drawing; counts use all units.
    const matchesFilters = useCallback((unit: Unit) =>
        (kindFilter === 'all' || unit.kind === kindFilter) &&
        (!vacantOnly || (isUnitLeasable(unit) && !isUnitRented(unit))), [kindFilter, vacantOnly]);
    const sections = useMemo(() => unitSections(sortedUnits.filter(matchesFilters), stairwells), [sortedUnits, matchesFilters, stairwells]);
    const visibleUnits = useMemo(() => levelUnits.filter(matchesFilters), [levelUnits, matchesFilters]);

    // The drawing pane shows the selected unit, else the level; placement keeps the level.
    const paneUnit = placement.kind === 'none' ? selectedUnit : undefined;
    const paneLease = paneUnit?.currentLeaseKey ? leaseHook.leases.find(l => l.key === paneUnit.currentLeaseKey) : undefined;
    // Phones show the list or the selected unit, never both.
    // Wide screens always show the detail pane: the selected unit, else a hint to pick one.
    const showDetailPane = useMediaQuery(DETAIL_PANE_QUERY);
    const mobileView = isSmallScreen && selectedUnit && placement.kind === 'none' ? 'detail' : 'list';
    const { containerRef, rememberListScroll } = useListDetailScroll<HTMLDivElement>(mobileView === 'detail');

    const unitMarkers = useMemo((): NumberedMarker[] => visibleUnits.map(unit => ({
        key: unit.key,
        number: unit.number,
        color: unitStatusColor(unit),
        icon: unitTypeIcon(unit.kind),
        iconColored: true,
        isRented: unitMarkerRented(unit),
        displayName: unitDisplayName(unit),
        // The pin is the centroid of the unit's placed rooms; only a single-room unit is dragged as a pin (plan D5).
        blueprintLocations: effectiveUnitLocations(unit, roomsMap),
        movable: !!currentBlueprintKey && !!movableUnitRoom(unit, roomsMap, currentBlueprintKey),
    })), [visibleUnits, roomsMap, currentBlueprintKey]);

    // Series defaults: the last created unit, else the highest-numbered unit of the filtered kind on the level.
    const seriesSource = useMemo(() => {
        const last = lastCreatedUnitKey ? units[lastCreatedUnitKey] : undefined;
        if (last && selectedLevelKey && unitLevelKeys(last, roomsMap).includes(selectedLevelKey)) return last;
        const sameKind = kindFilter === 'all' ? levelUnits : levelUnits.filter(unit => unit.kind === kindFilter);
        return sameKind[sameKind.length - 1];
    }, [lastCreatedUnitKey, units, levelUnits, selectedLevelKey, kindFilter, roomsMap]);

    // Memoised so re-renders while the modal is open do not reset the form.
    const newUnitInitialValues = useMemo((): Partial<UnitInput> | undefined => {
        if (showUnitModal !== 'new') return undefined;
        return {
            kind: seriesSource?.kind ?? (kindFilter === 'all' ? undefined : kindFilter),
            tenure: seriesSource?.tenure,
            number: seriesSource ? (nextNumberInSeries(seriesSource.number) ?? '') : '',
            area: seriesSource?.area,
            roomCount: seriesSource?.roomCount,
            commercialType: seriesSource?.commercialType,
            stairwellKey: seriesSource?.stairwellKey,
        };
    }, [showUnitModal, seriesSource, kindFilter]);

    const exitPlacement = useCallback(() => {
        setPlacement({ kind: 'none' });
        setPendingLocation(undefined);
    }, []);

    const handleBlueprintClick = useCallback(async (location: BlueprintLocation) => {
        if (!currentBlueprintKey) return;
        if (placement.kind === 'new') {
            setPendingLocation(location);
            setShowUnitModal('new');
        }
    }, [currentBlueprintKey, placement]);

    // A click in the list or on a marker selects (a second click on the selected row clears it); in placement
    // or move mode it only highlights the marker.
    const handleUnitClick = useCallback((unitKey: string) => {
        setSelectedUnitKey(placement.kind === 'none' && unitKey === selectedUnitKey ? null : unitKey);
    }, [placement.kind, selectedUnitKey, setSelectedUnitKey]);

    // Move mode: a dropped marker is written immediately; the mode stays on until "Klar".
    // Only a unit with exactly one room placed here is draggable, and dragging moves that room (plan D5).
    const handleUnitMove = useCallback((unitKey: string, location: BlueprintLocation) => {
        if (!currentBlueprintKey) return;
        setSelectedUnitKey(unitKey);
        const unit = units[unitKey];
        const room = unit ? movableUnitRoom(unit, roomsMap, currentBlueprintKey) : undefined;
        if (!room) return;
        moveUnitRoom(room.key, currentBlueprintKey, location).catch(() => undefined);
    }, [currentBlueprintKey, moveUnitRoom, setSelectedUnitKey, units, roomsMap]);

    if (loadingBuilding && !currentBuilding) return <PageLoader />;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding || !buildingKey) return <p>Byggnaden hittades ej</p>;

    const handleUnitSubmit = async (input: UnitCreateInput) => {
        if (showUnitModal === 'edit' && selectedUnit) {
            const edit: UnitInput = { ...input };
            await unitsHook.updateUnit(selectedUnit.key, edit);
            // A unit whose rooms no longer touch this level leaves the list; follow it so the modal keeps showing it.
            const nextLevels = unitLevelKeys({ rooms: Object.fromEntries(input.roomKeys.map(k => [k, true as const])) }, roomsMap);
            if (selectedLevelKey && nextLevels.length > 0 && !nextLevels.includes(selectedLevelKey)) {
                selectLevel(nextLevels[0], selectedUnit.key);
            }
            return;
        }
        // A click on the drawing places a whole-unit room there when no rooms were picked; otherwise the unit starts unplaced.
        const placement = selectedLevelKey && pendingLocation ? { levelKey: selectedLevelKey, location: pendingLocation } : undefined;
        const unit = await unitsHook.createUnit({ ...input, placement });
        setLastCreatedUnitKey(unit.key);
        setSelectedUnitKey(unit.key);
        setPendingLocation(undefined);
        // Series placement: stay in placement mode until the user stops it.
    };

    const handleUnitModalHide = () => {
        setShowUnitModal('closed');
        setPendingLocation(undefined);
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteKey) return;
        const unitKey = confirmDeleteKey;
        setConfirmDeleteKey(null);
        // Closed before the write so the detail modal does not come back for a unit being deleted.
        setUnitModalOpen(false);
        try {
            await unitsHook.deleteUnit(unitKey);
            if (selectedUnitKey === unitKey) setSelectedUnitKey(null);
        } catch {
            // Error is surfaced through the hook's error state.
        }
    };

    const handleLeaseSubmit = async ({ input, filesToUpload }: LeaseModalSubmitData) => {
        const lease = await leaseHook.createLease(input);
        const user = getAuth().currentUser;
        if (user) {
            for (const file of filesToUpload) {
                const result = await uploadLeaseContract(lease.key, file, user.uid);
                if (result.success && result.attachment) {
                    await leaseHook.addContract(lease.key, result.attachment);
                } else if (result.error) {
                    throw new Error(result.error);
                }
            }
        }
        setShowLeaseModal(false);
    };

    const handleDeletePhoto = async (photo: PhotoModel) => {
        if (!selectedUnit) return;
        await unitsHook.deleteUnitPhoto(selectedUnit.key, photo.key);
    };

    // The selected unit's details, as the right-most pane or the modal.
    const renderDetailProps = (unit: Unit): React.ComponentProps<typeof UnitDetail> => ({
        building: currentBuilding,
        unit,
        levels: levelsMap,
        rooms: roomsMap,
        isAdmin,
        submitting,
        lease: selectedLease,
        tenantName: selectedLease ? getTenantName(selectedLease.tenantKey) : undefined,
        endedLeases: selectedEndedLeases,
        getTenantName,
        stairwellName: unitStairwellName(unit, stairwells),
        leasesLink: isAdmin && buildingKey ? leasesPath(buildingKey, unit.currentLeaseKey) : undefined,
        onNewLease: () => setShowLeaseModal(true),
        onEdit: () => setShowUnitModal('edit'),
        onDelete: () => setConfirmDeleteKey(unit.key),
    });

    // Opened from the unit modal, which steps aside while they are open and returns after.
    const unitChildModalOpen = showUnitModal === 'edit' || showLeaseModal || confirmDeleteKey !== null;

    const apartmentCount = sortedUnits.filter(unit => unit.kind === UnitKind.Apartment).length;
    const commercialCount = sortedUnits.length - apartmentCount;
    const levelVacant = vacantUnitCount(levelUnits);
    const levelArea = totalUnitArea(levelUnits);
    const movableOnDrawing = !!currentBlueprintKey && levelUnits.some(u => !!movableUnitRoom(u, roomsMap, currentBlueprintKey));

    return (
        <>
            <BreadcrumbComponent
                items={[
                    { text: buildingName, link: `/building/${buildingKey}` },
                    { text: 'Lägenheter & lokaler' },
                ]}
            />

            <div className="units-page">
                <div className="units-title-container">
                    <h3>Lägenheter & lokaler</h3>
                    <span className="units-badge">{apartmentCount} lägenheter</span>
                    <span className="units-badge">{commercialCount} lokaler</span>
                </div>

                {error && <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>}
                {stairwellsHook.error && <Alert variant="danger" dismissible onClose={stairwellsHook.clearError}>{stairwellsHook.error}</Alert>}
                {leaseHook.error && <Alert variant="danger" dismissible onClose={leaseHook.clearError}>{leaseHook.error}</Alert>}

                <div className="units-split" ref={containerRef} data-mobile-view={mobileView}>
                    <div className="units-left-pane" onClickCapture={rememberListScroll}>
                        <div className="units-left-header">
                            <div className="units-section-header">
                                <h4>Enheter</h4>
                                {isAdmin && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => { setPendingLocation(undefined); setShowUnitModal('new'); }}
                                        disabled={submitting}
                                    >
                                        <Plus size={14} className="me-1" />Ny enhet
                                    </Button>
                                )}
                            </div>
                            <div className="units-filters">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    active={kindFilter === UnitKind.Apartment}
                                    onClick={() => setKindFilter(f => (f === UnitKind.Apartment ? 'all' : UnitKind.Apartment))}
                                    title="Visa bara lägenheter"
                                >
                                    Lägenheter
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    active={kindFilter === UnitKind.Commercial}
                                    onClick={() => setKindFilter(f => (f === UnitKind.Commercial ? 'all' : UnitKind.Commercial))}
                                    title="Visa bara lokaler"
                                >
                                    Lokaler
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    active={vacantOnly}
                                    onClick={() => setVacantOnly(v => !v)}
                                    title="Visa bara lediga hyresrätter"
                                >
                                    <KeyRound size={14} className="me-1" />Lediga
                                </Button>
                            </div>
                        </div>
                        <div className="units-left-scroll">
                            <UnitList
                                sections={sections}
                                selectedUnitKey={selectedUnitKey}
                                levels={levelsMap}
                                rooms={roomsMap}
                                onSelect={handleUnitClick}
                            />
                            {sortedStairwells.length > 0 && (
                                <StairwellsSection
                                    stairwells={sortedStairwells}
                                    units={sortedUnits}
                                    isAdmin={isAdmin}
                                    submitting={stairwellsHook.submitting}
                                    onCreate={stairwellsHook.createStairwell}
                                    onRename={stairwellsHook.renameStairwell}
                                    onDelete={stairwellsHook.deleteStairwell}
                                />
                            )}
                        </div>
                    </div>

                    <div className={`units-right-pane ${showDetailPane ? 'units-right-pane--with-detail' : ''}`}>
                        {mobileView === 'detail' && (
                            <button type="button" className="units-back-button" onClick={() => setSelectedUnitKey(null)}>
                                <ChevronLeft size={20} aria-hidden="true" />
                                Alla enheter
                            </button>
                        )}
                        {paneUnit ? (
                            <UnitDrawingsPane
                                key={paneUnit.key}
                                building={currentBuilding}
                                unit={paneUnit}
                                levels={levelsMap}
                                rooms={roomsMap}
                                stairwellName={unitStairwellName(paneUnit, stairwells)}
                                tenantName={isAdmin && paneLease ? getTenantName(paneLease.tenantKey) : undefined}
                                onShowDetails={showDetailPane ? undefined : () => setUnitModalOpen(true)}
                                onShowLevel={selectLevel}
                            />
                        ) : levels.length === 0 ? (
                            <div className="units-blueprint-placeholder">
                                <span>Inga våningsplan.</span>
                                <small>Lägenheter och lokaler hör till en eller flera våningar och placeras på deras ritningar — lägg till våningsplan under Hantera våningsplan i appen först.</small>
                            </div>
                        ) : (
                            <>
                                <LevelNavigator
                                    className="mb-3"
                                    levels={levels}
                                    selectedKey={selectedLevelKey ?? undefined}
                                    onSelect={selectLevel}
                                    getCount={key => unitsOnLevel(sortedUnits, key, roomsMap).length}
                                />

                                <div className="units-section-header">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h4>{level ? levelDisplayName(level) : 'Ritning'}</h4>
                                        <span className="units-badge">{levelUnits.length} enheter</span>
                                        <span className={`units-badge ${levelVacant > 0 ? 'units-badge--success' : 'units-badge--muted'}`}>Lediga: {levelVacant}</span>
                                        {levelArea > 0 && <span className="units-badge">Totalt {formatArea(levelArea)}</span>}
                                    </div>
                                    {isAdmin && hasDrawing && placement.kind === 'none' && (
                                        <div className="d-flex gap-2 flex-wrap">
                                            <Button variant="primary" size="sm" onClick={() => setPlacement({ kind: 'new' })} disabled={submitting}>
                                                <Plus size={14} className="me-1" />Placera nya
                                            </Button>
                                            {movableOnDrawing && (
                                                <Button variant="outline-primary" size="sm" onClick={() => setPlacement({ kind: 'move' })} disabled={submitting}>
                                                    <Move size={14} className="me-1" />Flytta markörer
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {placement.kind !== 'none' && (
                                    <div className={`units-placement-banner ${placement.kind === 'move' ? 'units-placement-banner--move' : ''}`}>
                                        <span>
                                            {placement.kind === 'new' && 'Klicka på ritningen där nästa lägenhet eller lokal ligger. Numret föreslås automatiskt.'}
                                            {placement.kind === 'move' && (
                                                <><strong>Flyttläge:</strong> dra en markör till rätt plats. Varje släpp sparas direkt. Enheter med flera rum på ritningen flyttas via rummen på våningssidan. Avsluta med Klar.</>
                                            )}
                                        </span>
                                        <Button variant="primary" size="sm" onClick={exitPlacement}>Klar</Button>
                                    </div>
                                )}

                                {hasDrawing && currentBlueprint && currentBlueprintKey ? (
                                    <BlueprintWithMarkers
                                        key={currentBlueprintKey}
                                        blueprint={currentBlueprint}
                                        currentBlueprintKey={currentBlueprintKey}
                                        building={currentBuilding}
                                        onRoomClick={(roomKey, event) => openPath(`/building/${buildingKey}/room/${roomKey}`, event)}
                                        spots={unitMarkers}
                                        selectedSpotKey={selectedUnitKey}
                                        onSpotClick={handleUnitClick}
                                        placementMode={placement.kind === 'new'}
                                        onBlueprintClick={handleBlueprintClick}
                                        onSpotMove={placement.kind === 'move' && isAdmin ? handleUnitMove : undefined}
                                        hideTitle
                                    />
                                ) : (
                                    <div className="units-blueprint-placeholder">
                                        <span>Ingen ritning uppladdad för denna våning.</span>
                                        {level && (
                                            <small>
                                                Enheter kan skapas i listan; deras rum placeras när våningen har en ritning.{' '}
                                                <Link to={`/building/${buildingKey}/level/${level.key}`}>Ladda upp ritning på våningssidan</Link>
                                            </small>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {showDetailPane && (
                        <div className="units-detail-column">
                            {selectedUnit ? (
                                <UnitDetail key={selectedUnit.key} {...renderDetailProps(selectedUnit)} variant="pane" />
                            ) : (
                                <div className="units-detail-empty">
                                    <MousePointerClick size={28} aria-hidden="true" />
                                    <span>Välj en lägenhet eller lokal i listan eller på ritningen för att visa den här.</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Narrow screens: mounted while open; hidden (state kept) while one of its child modals is open. */}
            {unitModalOpen && selectedUnit && !showDetailPane && (
                <UnitDetail
                    key={selectedUnit.key}
                    {...renderDetailProps(selectedUnit)}
                    show={!unitChildModalOpen}
                    onHide={() => setUnitModalOpen(false)}
                />
            )}

            <UnitModal
                show={showUnitModal !== 'closed'}
                onHide={handleUnitModalHide}
                onSubmit={handleUnitSubmit}
                levels={levelsMap}
                rooms={roomsMap}
                allUnits={sortedUnits}
                unit={showUnitModal === 'edit' ? selectedUnit : undefined}
                photoActions={showUnitModal === 'edit' && selectedUnit ? {
                    photos: selectedUnit.photos || {},
                    buildingKey: currentBuilding.key,
                    onDeletePhoto: handleDeletePhoto,
                    onUploadPhotos: uploader => unitsHook.uploadUnitPhotos(selectedUnit.key, uploader),
                } : undefined}
                initialValues={newUnitInitialValues}
                stairwells={sortedStairwells}
                // The form shows a failed create itself; keep the page alert behind it clear.
                onCreateStairwell={name => stairwellsHook.createStairwell(name).catch(err => { stairwellsHook.clearError(); throw err; })}
                placementLevelKey={showUnitModal === 'new' && pendingLocation ? selectedLevelKey ?? undefined : undefined}
                customerType={customerType}
                submitting={submitting}
            />

            {showLeaseModal && selectedUnit && (
                <LeaseModal
                    show={showLeaseModal}
                    onHide={() => setShowLeaseModal(false)}
                    onSubmit={handleLeaseSubmit}
                    buildingKey={buildingKey}
                    areas={[]}
                    rentalUnits={sortedUnits}
                    buildingLeases={leaseHook.leases}
                    lockedRentalUnit={{ unitKey: selectedUnit.key }}
                    submitting={leaseHook.submitting}
                />
            )}

            <Modal show={confirmDeleteKey !== null} onHide={() => setConfirmDeleteKey(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Är du säker?</Modal.Title>
                </Modal.Header>
                <Modal.Body>Enheten och dess foton tas bort. Ett tomt lägenhets- eller lokalrum som skapades med enheten tas också bort; andra rum finns kvar. Detta kan inte ångras.</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmDeleteKey(null)}>Avbryt</Button>
                    <Button variant="danger" onClick={handleConfirmDelete}>Ta bort</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UnitsPage;
